import { CircleDot, CircleX, WalletCards, X } from 'lucide-react';
import { round } from '@/utils/mathUtils';
import React, { memo, useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type {
  IItemInGroupForRequest,
  IOneItemInPrepareForRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import { useItemStore } from '@/store';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  item: IOneItemInPrepareForRequest;
};

interface ItemCardProps {
  item: IItemInGroupForRequest; // Using any temporarily to fix TypeScript errors
}

// Memoized card component for better performance
const ItemCard = memo(({ item: x }: ItemCardProps) => {
  const { openItemDialog } = useItemStore();

  const isActive =
    x?.RetailStock > 0 ||
    x?.WholeSaleStock > 0 ||
    x?.OtherStock > 0 ||
    x?.itemSalesRetail > 0 ||
    x?.itemSalesWholeSales > 0 ||
    x?.itemReturnsRetail > 0 ||
    x?.itemReturnsWholeSales > 0;

  const hasStock =
    x?.RetailStock > 0 || x?.WholeSaleStock > 0 || x?.OtherStock > 0;

  const hasDiscount = x?.retailDiscount > 0 || x?.wholesaleDiscount > 0;

  const hasSales = x?.itemSalesRetail > 0 || x?.itemSalesWholeSales > 0;

  const hasReturns = x?.itemReturnsRetail > 0 || x?.itemReturnsWholeSales > 0;

  const handleOpenDetails = useCallback(() => {
    openItemDialog(x?.ID);
  }, [openItemDialog, x?.ID]);

  return (
    <Card
      className={cn(
        'group relative z-10 overflow-hidden transition-all duration-200',
        'border border-border/40 bg-gradient-to-b from-background to-background/95',
        'hover:border-primary/20 hover:shadow-md hover:shadow-primary/5',
        'before:absolute before:inset-0 before:bg-gradient-to-br before:from-primary/5 before:to-transparent before:opacity-0 before:transition-opacity before:duration-200',
        'hover:before:opacity-100',
        'dark:border-border/20 dark:bg-gradient-to-b dark:from-gray-950/90 dark:to-gray-900/80',
      )}
    >
      <CardHeader className="space-y-0.5 pb-1.5">
        <CardTitle className="flex items-center justify-between rounded-md bg-primary/5 p-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                isActive
                  ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
              )}
            >
              {isActive ? (
                <CircleDot className="h-5 w-5 animate-pulse" />
              ) : (
                <CircleX className="h-5 w-5" />
              )}
            </div>
            <div className="flex flex-col">
              <span className="truncate text-base font-medium">
                {x?.Name || '—'}
              </span>
              <div className="flex items-center gap-1">
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                  الجمهور
                </span>
                <span className="text-xs text-muted-foreground">السعر</span>
              </div>
            </div>
          </div>
          <span className="rounded-md bg-primary/10 px-4 py-2 text-lg font-semibold text-primary">
            {round(x?.Price, 2)}
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-2 pt-0">
        <div className="flex flex-wrap gap-3">
          {/* Stats sections in a React.Fragment to improve performance */}
          {React.useMemo(
            () => (
              <>
                {/* Stock Section */}
                {hasStock && (
                  <div className="flex-1 space-y-0.5 rounded-md bg-gray-50/80 p-3 shadow-sm dark:bg-gray-800/40">
                    <h4 className="mb-2 border-b border-border/10 pb-1 text-sm font-medium text-muted-foreground">
                      المخزون
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {x?.RetailStock > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            رصيد ق
                          </span>
                          <span className="text-base font-semibold text-green-600 dark:text-green-400">
                            {round(x?.RetailStock, 2)}
                          </span>
                        </div>
                      )}
                      {x?.WholeSaleStock > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            رصيد ج
                          </span>
                          <span className="text-base font-semibold text-green-600 dark:text-green-400">
                            {round(x?.WholeSaleStock, 2)}
                          </span>
                        </div>
                      )}
                      {x?.OtherStock > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            رصيد ب
                          </span>
                          <span className="text-base font-semibold text-green-600 dark:text-green-400">
                            {round(x?.OtherStock, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Discount Section */}
                {hasDiscount && (
                  <div className="flex-1 space-y-0.5 rounded-md bg-gray-50/80 p-3 shadow-sm dark:bg-gray-800/40">
                    <h4 className="mb-2 border-b border-border/10 pb-1 text-sm font-medium text-muted-foreground">
                      الخصومات
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {x?.retailDiscount > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            خصم ق
                          </span>
                          <span className="text-base font-semibold text-yellow-600 dark:text-yellow-400">
                            {round(x?.retailDiscount, 2)}
                          </span>
                        </div>
                      )}
                      {x?.wholesaleDiscount > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            خصم ج
                          </span>
                          <span className="text-base font-semibold text-yellow-600 dark:text-yellow-400">
                            {round(x?.wholesaleDiscount, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sales Section */}
                {hasSales && (
                  <div className="flex-1 space-y-0.5 rounded-md bg-gray-50/80 p-3 shadow-sm dark:bg-gray-800/40">
                    <h4 className="mb-2 border-b border-border/10 pb-1 text-sm font-medium text-muted-foreground">
                      المبيعات
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {x?.itemSalesRetail > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            بيع ق
                          </span>
                          <span className="text-base font-semibold text-blue-600 dark:text-blue-400">
                            {round(x?.itemSalesRetail, 2)}
                          </span>
                        </div>
                      )}
                      {x?.itemSalesWholeSales > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            بيع ج
                          </span>
                          <span className="text-base font-semibold text-blue-600 dark:text-blue-400">
                            {round(x?.itemSalesWholeSales, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Returns Section */}
                {hasReturns && (
                  <div className="flex-1 space-y-0.5 rounded-md bg-gray-50/80 p-3 shadow-sm dark:bg-gray-800/40">
                    <h4 className="mb-2 border-b border-border/10 pb-1 text-sm font-medium text-muted-foreground">
                      المرتجعات
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {x?.itemReturnsRetail > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            مرتجع ق
                          </span>
                          <span className="text-base font-semibold text-red-600 dark:text-red-400">
                            {round(x?.itemReturnsRetail, 2)}
                          </span>
                        </div>
                      )}
                      {x?.itemReturnsWholeSales > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            مرتجع ج
                          </span>
                          <span className="text-base font-semibold text-red-600 dark:text-red-400">
                            {round(x?.itemReturnsWholeSales, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ),
            [
              hasStock,
              hasDiscount,
              hasSales,
              hasReturns,
              x?.RetailStock,
              x?.WholeSaleStock,
              x?.OtherStock,
              x?.retailDiscount,
              x?.wholesaleDiscount,
              x?.itemSalesRetail,
              x?.itemSalesWholeSales,
              x?.itemReturnsRetail,
              x?.itemReturnsWholeSales,
            ],
          )}
        </div>

        <Button
          variant="default"
          size="lg"
          className="relative z-20 mt-3 w-full bg-gradient-to-r from-primary/80 to-primary p-2 text-base font-medium text-primary-foreground shadow-md transition-all hover:from-primary hover:to-primary/90 hover:shadow-lg active:scale-[0.98]"
          onClick={handleOpenDetails}
        >
          عرض التفاصيل
        </Button>
      </CardContent>
    </Card>
  );
});

ItemCard.displayName = 'ItemCard';

function ItemsDialog({ closeFn, isOpen, item }: Props) {
  const sortedItems = React.useMemo(
    () => item?.GroupItems?.sort((a, b) => b?.Price - a?.Price) || [],
    [item?.GroupItems],
  );

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!sortedItems.length) return null;

    const totalItems = sortedItems.length;
    const activeItems = sortedItems.filter(
      (item) =>
        item.RetailStock > 0 ||
        item.WholeSaleStock > 0 ||
        item.OtherStock > 0 ||
        item.itemSalesRetail > 0 ||
        item.itemSalesWholeSales > 0,
    ).length;

    const avgPrice =
      sortedItems.reduce((sum, item) => sum + item.Price, 0) / totalItems;
    const maxPrice = Math.max(...sortedItems.map((item) => item.Price));
    const minPrice = Math.min(...sortedItems.map((item) => item.Price));

    return {
      totalItems,
      activeItems,
      avgPrice: round(avgPrice, 2),
      maxPrice: round(maxPrice, 2),
      minPrice: round(minPrice, 2),
    };
  }, [sortedItems]);

  return (
    <Dialog open={isOpen} onOpenChange={closeFn}>
      <DialogContent
        className="max-h-[90vh] overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 p-0 shadow-xl dark:from-gray-950 dark:to-gray-900 sm:max-w-3xl"
        overlayClass="backdrop-blur-sm bg-background/80"
      >
        <DialogHeader className="sticky top-0 z-30 border-b border-border/20 bg-white/95 p-4 backdrop-blur-sm dark:border-gray-800/30 dark:bg-gray-900/95">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-primary/20 to-primary/10 p-2 shadow-sm dark:from-primary/30 dark:to-primary/10">
                <WalletCards className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">
                  تفصيل الصنف
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm text-muted-foreground">
                  {item?.itemName}
                </DialogDescription>
              </div>
            </div>

            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-md hover:bg-destructive/10 hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>

          {stats && (
            <div className="mt-3 grid grid-cols-3 gap-2 rounded-md bg-gradient-to-br from-primary/10 to-primary/5 p-2 text-center shadow-sm dark:from-primary/20 dark:to-primary/5">
              <div className="rounded-md p-1 transition-colors hover:bg-white/40 dark:hover:bg-white/5">
                <p className="text-xs text-muted-foreground">عدد الأصناف</p>
                <p className="font-semibold text-primary">{stats.totalItems}</p>
              </div>
              <div className="rounded-md p-1 transition-colors hover:bg-white/40 dark:hover:bg-white/5">
                <p className="text-xs text-muted-foreground">متوسط السعر</p>
                <p className="font-semibold text-primary">{stats.avgPrice}</p>
              </div>
              <div className="rounded-md p-1 transition-colors hover:bg-white/40 dark:hover:bg-white/5">
                <p className="text-xs text-muted-foreground">نشط / الكل</p>
                <p className="font-semibold text-primary">
                  {stats.activeItems} / {stats.totalItems}
                </p>
              </div>
            </div>
          )}
        </DialogHeader>

        <ScrollArea
          className="relative z-10 h-[calc(100vh-12rem)] px-4"
          type="always"
        >
          <div className="grid grid-cols-1 gap-4 py-4">
            {sortedItems.map((groupItem) => (
              <ItemCard key={groupItem.ID} item={groupItem} />
            ))}

            {sortedItems.length === 0 && (
              <div className="col-span-full flex h-32 items-center justify-center rounded-md border border-dashed border-border/50 p-6 text-center">
                <div className="flex flex-col items-center">
                  <WalletCards className="mb-2 h-10 w-10 text-muted-foreground/50" />
                  <p className="text-muted-foreground">
                    لا يوجد مجموعة لهذا الصنف
                  </p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default memo(ItemsDialog);
